/**
 * Cache Utilities for Blog Content
 * Implements content caching, cache invalidation, and performance optimizations
 */

// Cache configuration
const CACHE_CONFIG = {
  // Cache durations in milliseconds
  BLOG_POST_TTL: 15 * 60 * 1000, // 15 minutes
  BLOG_LIST_TTL: 10 * 60 * 1000, // 10 minutes
  CATEGORY_TTL: 30 * 60 * 1000,  // 30 minutes
  SEARCH_TTL: 5 * 60 * 1000,     // 5 minutes
  IMAGE_TTL: 60 * 60 * 1000,     // 1 hour
  
  // Cache size limits
  MAX_CACHE_SIZE: 100, // Maximum number of cached items
  MAX_SEARCH_CACHE_SIZE: 50,
  
  // Cache keys
  KEYS: {
    BLOG_POST: 'blog_post_',
    BLOG_LIST: 'blog_list_',
    CATEGORY: 'category_',
    SEARCH: 'search_',
    FEATURED: 'featured_blogs',
    RELATED: 'related_',
    CATEGORIES_LIST: 'categories_list'
  }
};

/**
 * In-memory cache implementation
 */
class MemoryCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.accessCount = new Map();
  }

  /**
   * Set cache item with TTL
   */
  set(key, value, ttl = CACHE_CONFIG.BLOG_POST_TTL) {
    const now = Date.now();
    
    // Remove expired items if cache is full
    if (this.cache.size >= CACHE_CONFIG.MAX_CACHE_SIZE) {
      this.cleanup();
    }
    
    this.cache.set(key, value);
    this.timestamps.set(key, now + ttl);
    this.accessCount.set(key, 0);
    
    return true;
  }

  /**
   * Get cache item if not expired
   */
  get(key) {
    const now = Date.now();
    const expiry = this.timestamps.get(key);
    
    if (!expiry || now > expiry) {
      // Item expired or doesn't exist
      this.delete(key);
      return null;
    }
    
    // Update access count for LRU
    const currentCount = this.accessCount.get(key) || 0;
    this.accessCount.set(key, currentCount + 1);
    
    return this.cache.get(key);
  }

  /**
   * Delete cache item
   */
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
    this.accessCount.delete(key);
    return true;
  }

  /**
   * Check if cache has valid item
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
    this.accessCount.clear();
  }

  /**
   * Clean up expired items and enforce size limits
   */
  cleanup() {
    const now = Date.now();
    
    // Remove expired items
    for (const [key, expiry] of this.timestamps.entries()) {
      if (now > expiry) {
        this.delete(key);
      }
    }
    
    // If still over limit, remove least recently used items
    if (this.cache.size >= CACHE_CONFIG.MAX_CACHE_SIZE) {
      const sortedByAccess = Array.from(this.accessCount.entries())
        .sort((a, b) => a[1] - b[1])
        .slice(0, Math.floor(CACHE_CONFIG.MAX_CACHE_SIZE * 0.2)); // Remove 20% of items
      
      sortedByAccess.forEach(([key]) => this.delete(key));
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: CACHE_CONFIG.MAX_CACHE_SIZE,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Global cache instance
const cache = new MemoryCache();

/**
 * Generate cache key for blog post
 */
export const getBlogPostCacheKey = (slug) => {
  return `${CACHE_CONFIG.KEYS.BLOG_POST}${slug}`;
};

/**
 * Generate cache key for blog list
 */
export const getBlogListCacheKey = (params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}:${params[key]}`)
    .join('|');
  return `${CACHE_CONFIG.KEYS.BLOG_LIST}${sortedParams}`;
};

/**
 * Generate cache key for category
 */
export const getCategoryCacheKey = (categorySlug, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}:${params[key]}`)
    .join('|');
  return `${CACHE_CONFIG.KEYS.CATEGORY}${categorySlug}|${sortedParams}`;
};

/**
 * Generate cache key for search
 */
export const getSearchCacheKey = (query, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}:${params[key]}`)
    .join('|');
  return `${CACHE_CONFIG.KEYS.SEARCH}${query}|${sortedParams}`;
};

/**
 * Cache blog post
 */
export const cacheBlogPost = (slug, blogPost) => {
  const key = getBlogPostCacheKey(slug);
  return cache.set(key, blogPost, CACHE_CONFIG.BLOG_POST_TTL);
};

/**
 * Get cached blog post
 */
export const getCachedBlogPost = (slug) => {
  const key = getBlogPostCacheKey(slug);
  return cache.get(key);
};

/**
 * Cache blog list
 */
export const cacheBlogList = (params, blogList) => {
  const key = getBlogListCacheKey(params);
  return cache.set(key, blogList, CACHE_CONFIG.BLOG_LIST_TTL);
};

/**
 * Get cached blog list
 */
export const getCachedBlogList = (params) => {
  const key = getBlogListCacheKey(params);
  return cache.get(key);
};

/**
 * Cache category blogs
 */
export const cacheCategoryBlogs = (categorySlug, params, blogList) => {
  const key = getCategoryCacheKey(categorySlug, params);
  return cache.set(key, blogList, CACHE_CONFIG.BLOG_LIST_TTL);
};

/**
 * Get cached category blogs
 */
export const getCachedCategoryBlogs = (categorySlug, params) => {
  const key = getCategoryCacheKey(categorySlug, params);
  return cache.get(key);
};

/**
 * Cache search results
 */
export const cacheSearchResults = (query, params, results) => {
  const key = getSearchCacheKey(query, params);
  return cache.set(key, results, CACHE_CONFIG.SEARCH_TTL);
};

/**
 * Get cached search results
 */
export const getCachedSearchResults = (query, params) => {
  const key = getSearchCacheKey(query, params);
  return cache.get(key);
};

/**
 * Cache categories list
 */
export const cacheCategories = (categories) => {
  return cache.set(CACHE_CONFIG.KEYS.CATEGORIES_LIST, categories, CACHE_CONFIG.CATEGORY_TTL);
};

/**
 * Get cached categories
 */
export const getCachedCategories = () => {
  return cache.get(CACHE_CONFIG.KEYS.CATEGORIES_LIST);
};

/**
 * Cache featured blogs
 */
export const cacheFeaturedBlogs = (blogs) => {
  return cache.set(CACHE_CONFIG.KEYS.FEATURED, blogs, CACHE_CONFIG.BLOG_LIST_TTL);
};

/**
 * Get cached featured blogs
 */
export const getCachedFeaturedBlogs = () => {
  return cache.get(CACHE_CONFIG.KEYS.FEATURED);
};

/**
 * Cache related blogs
 */
export const cacheRelatedBlogs = (blogId, relatedBlogs) => {
  const key = `${CACHE_CONFIG.KEYS.RELATED}${blogId}`;
  return cache.set(key, relatedBlogs, CACHE_CONFIG.BLOG_LIST_TTL);
};

/**
 * Get cached related blogs
 */
export const getCachedRelatedBlogs = (blogId) => {
  const key = `${CACHE_CONFIG.KEYS.RELATED}${blogId}`;
  return cache.get(key);
};

/**
 * Invalidate cache for specific blog post
 */
export const invalidateBlogPost = (slug) => {
  const key = getBlogPostCacheKey(slug);
  cache.delete(key);
  
  // Also invalidate related caches
  invalidateBlogLists();
  invalidateFeaturedBlogs();
};

/**
 * Invalidate all blog list caches
 */
export const invalidateBlogLists = () => {
  const keys = cache.cache.keys();
  for (const key of keys) {
    if (key.startsWith(CACHE_CONFIG.KEYS.BLOG_LIST) || 
        key.startsWith(CACHE_CONFIG.KEYS.CATEGORY)) {
      cache.delete(key);
    }
  }
};

/**
 * Invalidate search caches
 */
export const invalidateSearchCaches = () => {
  const keys = cache.cache.keys();
  for (const key of keys) {
    if (key.startsWith(CACHE_CONFIG.KEYS.SEARCH)) {
      cache.delete(key);
    }
  }
};

/**
 * Invalidate featured blogs cache
 */
export const invalidateFeaturedBlogs = () => {
  cache.delete(CACHE_CONFIG.KEYS.FEATURED);
};

/**
 * Invalidate categories cache
 */
export const invalidateCategories = () => {
  cache.delete(CACHE_CONFIG.KEYS.CATEGORIES_LIST);
};

/**
 * Invalidate all caches
 */
export const invalidateAllCaches = () => {
  cache.clear();
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  return cache.getStats();
};

/**
 * Preload critical content
 */
export const preloadCriticalContent = async (blogService) => {
  try {
    // Preload featured blogs
    if (!getCachedFeaturedBlogs()) {
      const featuredData = await blogService.getFeaturedBlogs(3);
      cacheFeaturedBlogs(featuredData);
    }
    
    // Preload categories
    if (!getCachedCategories()) {
      const categoriesData = await blogService.getCategories();
      cacheCategories(categoriesData);
    }
    
    // Preload first page of blogs
    const firstPageParams = { page: 1, limit: 6 };
    if (!getCachedBlogList(firstPageParams)) {
      const blogsData = await blogService.getBlogs(firstPageParams);
      cacheBlogList(firstPageParams, blogsData);
    }
    
  } catch (error) {
    console.error('Error preloading critical content:', error);
  }
};

/**
 * Setup automatic cache cleanup
 */
export const setupCacheCleanup = () => {
  // Clean up expired items every 5 minutes
  setInterval(() => {
    cache.cleanup();
  }, 5 * 60 * 1000);
};

// Initialize cache cleanup on module load
setupCacheCleanup();

export default {
  cacheBlogPost,
  getCachedBlogPost,
  cacheBlogList,
  getCachedBlogList,
  cacheCategoryBlogs,
  getCachedCategoryBlogs,
  cacheSearchResults,
  getCachedSearchResults,
  cacheCategories,
  getCachedCategories,
  cacheFeaturedBlogs,
  getCachedFeaturedBlogs,
  cacheRelatedBlogs,
  getCachedRelatedBlogs,
  invalidateBlogPost,
  invalidateBlogLists,
  invalidateSearchCaches,
  invalidateFeaturedBlogs,
  invalidateCategories,
  invalidateAllCaches,
  getCacheStats,
  preloadCriticalContent,
  setupCacheCleanup
};

// CommonJS exports for testing compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    cacheBlogPost,
    getCachedBlogPost,
    cacheBlogList,
    getCachedBlogList,
    cacheCategoryBlogs,
    getCachedCategoryBlogs,
    cacheSearchResults,
    getCachedSearchResults,
    cacheCategories,
    getCachedCategories,
    cacheFeaturedBlogs,
    getCachedFeaturedBlogs,
    cacheRelatedBlogs,
    getCachedRelatedBlogs,
    invalidateBlogPost,
    invalidateBlogLists,
    invalidateSearchCaches,
    invalidateFeaturedBlogs,
    invalidateCategories,
    invalidateAllCaches,
    getCacheStats,
    preloadCriticalContent,
    setupCacheCleanup
  };
}